/* eslint-disable max-lines-per-function, max-statements */
import prepareComponent from "@bluframe/grapple/prepareComponent";
import config from "config/site";
import useActionsCheckbox from "hooks/actions/useActionsCheckbox";
import useNewsAddActions from "hooks/news/useNewsAddActions";
import useNewsCard from "hooks/news/useNewsCard";
import useCreateNewsCritics from "hooks/useCreateNewsCritics";
import useNewsCritics from "hooks/useNewsCritics";
import useUser from "hooks/useUser";
import { useCallback, useMemo, useState } from "react";
import Add from "./Add";
import useNewsServiceListControls from "./hooks/useNewsServiceListControls";
const DEFAULT_IS_COMPACT = true;
export const usePrepareComponent = () => {
    const { clear, setIsAddHomeVideo, setIsUHD, setIMDbId, setOverrides, state: { avNirvana, bluRay, hidefDigest, news, whySoBlu } } = useNewsCritics();
    const { isAdmin, isEditor } = useUser();
    const [isCompact, setIsCompact] = useState(DEFAULT_IS_COMPACT);
    const [isSingleSource, setIsSingleSource] = useState(false);
    const { areNewsSourcesLoading } = useCreateNewsCritics();
    const newsAddActionsProps = useNewsAddActions();
    const onClear = useCallback((serviceName) => () => {
        // Clear review
        clear(serviceName);
    }, [clear]);
    const onSetOverrides = useCallback((serviceName) => (overrides) => {
        setOverrides(overrides, serviceName);
    }, [setOverrides]);
    const newsControlCheckboxesProps = useMemo(() => {
        const isAddHomeVideoProps = {
            checked: news.isAddHomeVideo,
            isDisabled: areNewsSourcesLoading,
            onChange: () => {
                setIsAddHomeVideo(!news.isAddHomeVideo);
            }
        };
        const isCompactCheckboxProps = {
            checked: isCompact,
            isDisabled: areNewsSourcesLoading,
            onChange: () => {
                setIsCompact(!isCompact);
            }
        };
        const isSingleSourceCheckboxProps = {
            checked: isSingleSource,
            isDisabled: areNewsSourcesLoading,
            onChange: () => {
                // When switching to multi-source, reset all OMDb IDs
                setIsSingleSource(!isSingleSource);
            }
        };
        const isUHDCheckboxProps = {
            checked: news.isUHD ?? false,
            isDisabled: areNewsSourcesLoading,
            onChange: () => {
                setIsUHD(!news.isUHD);
            }
        };
        return {
            isAddHomeVideoProps,
            isAdmin,
            isCompactCheckboxProps,
            isEditor,
            isSingleSourceCheckboxProps,
            isUHDCheckboxProps: news.isAddHomeVideo ? isUHDCheckboxProps : undefined
        };
    }, [
        areNewsSourcesLoading,
        isAdmin,
        isCompact,
        isEditor,
        isSingleSource,
        news.isAddHomeVideo,
        news.isUHD,
        setIsAddHomeVideo,
        setIsUHD
    ]);
    /**
     *  List of news service names from config file.
     */
    const newsServiceNames = useMemo(() => Object.freeze(config.news
        .flatMap((item) => item.rows)
        .map((row) => ({ name: row.name, serviceName: row.serviceName }))), []);
    const actionCheckboxes = newsServiceNames.reduce((acc, { serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const onlyNewProps = useActionsCheckbox({
            inputId: `${serviceName}-isOnlyNew`,
            label: "Only New",
            serviceName,
            serviceType: "news",
            setValueName: "setIsOnlyNew",
            valueName: "isOnlyNew"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const onlyPresentProps = useActionsCheckbox({
            inputId: `${serviceName}-isOnlyPresent`,
            label: "Only Present",
            serviceName,
            serviceType: "news",
            setValueName: "setIsOnlyPresent",
            valueName: "isOnlyPresent"
        });
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const showFlaggedProps = useActionsCheckbox({
            inputId: `${serviceName}-isShowFlagged`,
            label: "Show Flagged",
            serviceName,
            serviceType: "news",
            setValueName: "setIsShowFlagged",
            valueName: "isShowFlagged"
        });
        acc[serviceName] = {
            onlyNewProps,
            onlyPresentProps,
            showFlaggedProps
        };
        return acc;
    }, {});
    const newsCards = newsServiceNames.reduce((acc, { name, serviceName }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        acc[serviceName] = useNewsCard({ name, serviceName, setIMDbId });
        return acc;
    }, {});
    const { clearAllListsButtonProps, clearListButtonProps, listAllButtonProps, listButtonProps, loadMoreButtonProps } = useNewsServiceListControls(newsServiceNames);
    return {
        actionCheckboxes,
        areNewsSourcesLoading,
        avNirvana: avNirvana.selected,
        bluRay: bluRay.selected,
        clearAllListsButtonProps,
        clearListButtonProps,
        hidefDigest: hidefDigest.selected,
        isAdmin,
        isEditor,
        listAllButtonProps,
        listButtonProps,
        loadMoreButtonProps,
        news,
        newsAddActionsProps,
        newsCards,
        newsControlCheckboxesProps,
        onClear,
        onSetOverrides,
        siteConfigNews: config.news,
        whySoBlu: whySoBlu.selected
    };
};
export default prepareComponent(usePrepareComponent)(Add);
